import {
  EgiAppsSynchronizer,
  FaultyClickTroughConfiguration
} from '@/app/platform/platform-data/synchronizers/types';
import { Inject, Injectable } from '@angular/core';
import { ErrorInformation } from '@shared/models/error/error-information';
import {
  EGI_APP_SERVICE,
  EgiAppService,
  NAVIGATION_ITEM_SERVICE,
  NavigationItemService,
  USER_GROUP_SERVICE,
  UserGroupService
} from '@/app/platform/platform-data/services/types';
import { EgiPage, EgiPagesSnapshot } from '@ervo/page-framework';
import { err, ok } from '@common/result/Result';
import { allSucceeded, extractErrors, extractValues, hasError } from '@common/result/result-utils';
import { NavigationItemType } from '@shared/enum/navigation-item-type';
import { AsyncResult } from '@common/result/asyncResult';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { ErrorReason } from '@shared/models/error/error-reason';

@Injectable({
  providedIn: 'root'
})
export class ApiEgiAppSynchronizer implements EgiAppsSynchronizer {
  private readonly egiPages: EgiPage[];
  private readonly egiHomePage: EgiPage;

  constructor(
    @Inject(EGI_APP_SERVICE)
    private readonly egiAppService: EgiAppService,
    @Inject(NAVIGATION_ITEM_SERVICE)
    private readonly navigationItemService: NavigationItemService,
    egiPagesSnapshot: EgiPagesSnapshot,
    @Inject(USER_GROUP_SERVICE)
    private readonly userGroupService: UserGroupService
  ) {
    this.egiPages = egiPagesSnapshot.pages;
    this.egiHomePage = egiPagesSnapshot.homePage;
  }

  async synchronizeApps(): AsyncResult<
    EgiAppEntity[],
    ErrorInformation | ErrorInformation<FaultyClickTroughConfiguration[]>
  > {
    const faultyConfiguredIClickTroughPages = this.findFaultyConfiguredClicktroughPages();

    if (faultyConfiguredIClickTroughPages.length) {
      const error = new ErrorInformation(
        'One or more configured clickTroughPages do not exist!',
        ErrorReason.Unimplemented,
        faultyConfiguredIClickTroughPages
      );
      return err([error]);
    }

    const result = await this.loadAppsAndGroups();
    if (result.hasFailed) {
      return err(result.errors);
    }

    const [apps, groups] = result.value as [EgiAppEntity[], UserGroupEntity[]];

    const promises = apps.map(app => this.synchronizeApp(app, groups));
    const results = await Promise.all(promises);
    if (hasError(results)) {
      return err(extractErrors(results));
    }

    const syncedApps = extractValues(results);

    const notCompletelySynchronized = syncedApps.some(app => app.shouldBeSynced);
    if (notCompletelySynchronized) {
      return this.synchronizeApps();
    }

    syncedApps.forEach(a => a.rootNavigationItem.sortDescendants());
    return ok(syncedApps);
  }

  async synchronizeApp(
    app: EgiAppEntity,
    groups: UserGroupEntity[]
  ): AsyncResult<EgiAppEntity, ErrorInformation> {
    const { rootNavigationItem } = app;

    const items = rootNavigationItem.asArray.filter(i => i.type !== NavigationItemType.Menu);

    const requests: AsyncResult<NavigationItemEntity, ErrorInformation>[] = items.map(i =>
      this.synchronizeNavigationItem(i, groups)
    );
    const results = await Promise.all(requests);

    if (hasError(results)) {
      return err(extractErrors(results));
    }

    if (rootNavigationItem.lacksEgiPage) {
      const result = await this.setDefaultHomePageAsPage(app);
      if (result.hasFailed) {
        return result;
      }
    }

    return this.egiAppService.getById(app.id);
  }

  private async setDefaultHomePageAsPage(
    app: EgiAppEntity
  ): AsyncResult<EgiAppEntity, ErrorInformation> {
    const clone = app.clone();
    clone.rootNavigationItem.pageComponentId = this.egiHomePage.config.id;
    return await this.egiAppService.update(clone);
  }

  async synchronizeNavigationItem(
    item: NavigationItemEntity,
    groups: UserGroupEntity[]
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    if (item.lacksEgiPage && !item.isClickTroughPage && !item.isRoot) {
      return await this.navigationItemService.delete(item);
    }

    const requests = [
      this.removeObsoleteClickThroughPages(item, groups),
      this.addMissingClickThroughPages(item)
    ];
    const results = await Promise.all(requests);

    if (hasError(results)) {
      return err(extractErrors(results));
    }

    return ok(item);
  }

  private async removeObsoleteClickThroughPages(
    parent: NavigationItemEntity,
    groups: UserGroupEntity[]
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const clickThroughPageIds = parent.clickTroughPageIds;

    const { clickThroughChildren } = parent;
    const obsoleteClickThroughChildren = clickThroughChildren.filter(
      c => !clickThroughPageIds.includes(c.pageComponentId)
    );

    const promises = obsoleteClickThroughChildren.map(c => {
      this.removeAuthorizationForObsoleteClickTroughPage(parent.id, c, groups);
      return this.navigationItemService.delete(c);
    });
    const results = await Promise.all(promises);
    if (hasError(results)) {
      return err(extractErrors(results));
    }

    if (!results.length) {
      return ok(parent);
    }

    const updatedItem = extractValues(results)[0];
    return ok(updatedItem);
  }

  private async removeAuthorizationForObsoleteClickTroughPage(
    parentId: string,
    clickTroughItem: NavigationItemEntity,
    groups: UserGroupEntity[]
  ): AsyncResult<boolean, ErrorInformation> {
    const allAuthorizations = groups.map(group => group.authorizations).flat();

    const obsoleteAuthorizations = allAuthorizations.filter(
      authorization =>
        authorization.navigationItemId === parentId &&
        authorization.featureId === clickTroughItem.pageComponentId
    );

    const requests = obsoleteAuthorizations.map(a =>
      this.userGroupService.unAuthorizePageFeature(a)
    );

    const results = await Promise.all(requests);

    if (allSucceeded(results)) {
      return ok(true);
    }

    return err(extractErrors(results));
  }

  private async addMissingClickThroughPages(
    item: NavigationItemEntity
  ): AsyncResult<NavigationItemEntity, ErrorInformation> {
    const missingClickThroughPageIds = item.missingClickThroughPageIds;

    if (!missingClickThroughPageIds.length) {
      return ok(item);
    }

    const promises = missingClickThroughPageIds.map(async pageId => {
      const page = this.egiPages.find(p => p.config.id === pageId);
      const navigationItemToCreate = NavigationItemEntity.createClickThroughPage(item, page);
      const result = await this.navigationItemService.create(navigationItemToCreate);
      if (result.hasFailed) {
        return result;
      }

      return result;
    });

    const results = await Promise.all(promises);

    if (hasError(results)) {
      return err(extractErrors(results));
    }

    const resultValues = extractValues(results);
    return ok(resultValues[0] || NavigationItemEntity.createDefault());
  }

  private async loadAppsAndGroups(): AsyncResult<
    [EgiAppEntity[], UserGroupEntity[]],
    ErrorInformation
  > {
    const appsResult = await this.egiAppService.getAll();
    if (appsResult.hasFailed) {
      return err(appsResult.errors);
    }
    const apps = appsResult.value as EgiAppEntity[];

    const groupsResult = await this.userGroupService.getAll();
    if (groupsResult.hasFailed) {
      return err(groupsResult.errors);
    }

    const groups = groupsResult.value as UserGroupEntity[];
    const tuple: [EgiAppEntity[], UserGroupEntity[]] = [apps, groups];
    return ok(tuple);
  }

  private findFaultyConfiguredClicktroughPages(): FaultyClickTroughConfiguration[] {
    const configuredComponentIds = this.egiPages.map(p => p.config.id);

    const faultyConfigurations: FaultyClickTroughConfiguration[] = [];
    this.egiPages.forEach(page => {
      const clickTroughIds = this.mapClickTroughIdsToArray(page);
      const faultyIds = clickTroughIds.filter(id => !configuredComponentIds.includes(id));

      faultyIds.forEach(faultyId => {
        faultyConfigurations.push({
          faultyId,
          page
        });
      });
    });

    return faultyConfigurations;
  }

  private mapClickTroughIdsToArray(page: EgiPage): string[] {
    if (!page.config.clickTroughPages) {
      return [];
    }

    const { clickTroughPages } = page.config;
    return Object.keys(clickTroughPages).map(k => clickTroughPages[k].id);
  }
}
